import { Button as AntButton, Select } from "antd"
import { useEffect, useState } from "react"
import { Button as BootstrapButton, Col, Form, Modal, Row } from "react-bootstrap"
import CheckGroup from "../Common/components/CheckGroup"
import { DollarInputV2 } from "./DollarInputV2"
import { Tips } from "../Common/Tooltip"
import * as com from "../Common"
import "./styles.css"

const FieldError = ({ message }) => (
    message ? <div className="warning-msg">{message}</div> : null
)

const MODAL_FIELD_ORDER = ["amount", "incometype"]

const scrollToField = (fieldKey) => {
    if (!fieldKey || typeof document === "undefined") return
    const el = document.querySelector(`[data-field="${fieldKey}"]`)
    if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
}

const emptyIncome = {
    hasdividends: false,
    dividendsmonthly: "",
    dividends: "",
    netrentalmonthly: "",
    netrental: "",
    otherincomemonthly: "",
    otherincome: [],
}

const normalizeIncome = (income = {}) => ({
    ...emptyIncome,
    ...income,
    otherincome: Array.isArray(income.otherincome) ? income.otherincome : [],
})

const formatAmount = (value) => (
    value ? Number(value).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }) : "--"
)

const incomeDescriptionFromType = (value) => {
    const source = com.othersouresofincome.find((item) => item[0] === value)
    if (!source || value === "Other") {
        return ""
    }
    return source[1]
}

const incomeTypeLabel = (item) => {
    if (item?.description) {
        return item.description
    }
    const source = com.othersouresofincome.find((entry) => entry[0] === item?.incometype)
    return source ? source[1] : "Other"
}

const isIncompleteOtherIncome = (item) => (
    item == null
    || item.amount === ""
    || item.amount == null
    || !item.incometype
)

const validateOtherIncomeDraft = (draft) => {
    const errors = {}
    if (draft.amount === "" || draft.amount == null) {
        errors.amount = "Please provide income."
    }
    if (!draft.incometype) {
        errors.incometype = "Please select an income type."
    }
    const firstErrorKey = MODAL_FIELD_ORDER.find((key) => !!errors[key]) || null
    return { errors, firstErrorKey }
}

const OtherIncomeV2 = ({ participant, saving, onSave, onPersist, onBack }) => {
    const [income, setIncome] = useState(emptyIncome)
    const [serveArmedForces, setServeArmedForces] = useState("no")
    const [showModal, setShowModal] = useState(false)
    const [editIndex, setEditIndex] = useState(-1)
    const [draft, setDraft] = useState({ amount: "", incometype: "", description: "", period: "monthly" })
    const [modalFieldErrors, setModalFieldErrors] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})

    useEffect(() => {
        const normalized = normalizeIncome(participant?.income)
        setIncome(normalized)
        // Legacy UI treats empty serveArmedForces as No.
        setServeArmedForces(participant?.serveArmedForces === "yes" ? "yes" : "no")
        setFieldErrors({})
        setModalFieldErrors({})
    }, [participant])

    const clearModalFieldError = (field) => {
        setModalFieldErrors((prev) => {
            if (!prev[field]) return prev
            const next = { ...prev }
            delete next[field]
            return next
        })
    }

    const hasOtherIncome = income.otherincome.length > 0

    const openModal = (index = -1) => {
        const existing = index >= 0 ? income.otherincome[index] : null
        setEditIndex(index)
        setDraft({
            amount: existing?.amount || "",
            incometype: existing?.incometype || "",
            description: existing?.description || "",
            period: existing?.period || "monthly",
        })
        setModalFieldErrors({})
        setShowModal(true)
    }

    const closeModal = () => {
        setShowModal(false)
        setModalFieldErrors({})
        // Drop unfinished shells created when selecting Yes (legacy left them; V2 cleans up).
        setIncome((prev) => ({
            ...prev,
            otherincome: prev.otherincome.filter((item) => !isIncompleteOtherIncome(item)),
        }))
    }

    const saveDraft = async (event) => {
        event.preventDefault()
        event.stopPropagation()
        // English field errors only — no reportValidity / browser-locale messages.
        const { errors, firstErrorKey } = validateOtherIncomeDraft(draft)
        if (firstErrorKey) {
            setModalFieldErrors(errors)
            setTimeout(() => scrollToField(firstErrorKey), 50)
            return
        }
        setModalFieldErrors({})

        const nextItem = {
            key: editIndex >= 0 ? (income.otherincome[editIndex]?.key || `${Date.now()}`) : `${Date.now()}`,
            amount: `${draft.amount}`,
            incometype: draft.incometype,
            description: draft.description || incomeDescriptionFromType(draft.incometype),
            period: "monthly",
        }
        const otherincome = [...income.otherincome]
        if (editIndex >= 0) {
            otherincome[editIndex] = nextItem
        } else {
            otherincome.push(nextItem)
        }
        const nextIncome = { ...income, otherincome }
        if (onPersist) {
            const persisted = await onPersist({
                income: nextIncome,
                serveArmedForces: serveArmedForces === "yes" ? "yes" : "no",
            })
            if (persisted === false) {
                return
            }
        }
        setIncome(nextIncome)
        setFieldErrors({})
        setShowModal(false)
    }

    const setHasOtherIncome = (value) => {
        setFieldErrors({})
        if (value === "true") {
            if (!hasOtherIncome) {
                // Legacy prepends an empty row then opens the modal so Yes stays selected.
                setIncome((prev) => ({
                    ...prev,
                    otherincome: [{
                        key: `${Date.now()}`,
                        period: "monthly",
                        amount: "",
                        incometype: "",
                        description: "",
                    }, ...prev.otherincome],
                }))
                setEditIndex(0)
                setDraft({ amount: "", incometype: "", description: "", period: "monthly" })
                setModalFieldErrors({})
                setShowModal(true)
            }
            return
        }
        setIncome((prev) => ({ ...prev, otherincome: [] }))
    }

    const setHasDividends = (value) => {
        const hasDividends = value === "true"
        setIncome((prev) => ({
            ...prev,
            hasdividends: hasDividends,
            ...(hasDividends ? {} : { dividendsmonthly: "", dividends: "" }),
        }))
    }

    const removeOtherIncome = (index) => {
        setIncome((prev) => ({
            ...prev,
            otherincome: prev.otherincome.filter((_, itemIndex) => itemIndex !== index),
        }))
        setFieldErrors({})
    }

    const handleNext = () => {
        if (income.otherincome.some(isIncompleteOtherIncome)) {
            setFieldErrors({
                otherincome: "Please complete or remove incomplete other income entries.",
            })
            setTimeout(() => scrollToField("otherincome"), 50)
            return
        }
        setFieldErrors({})
        onSave({
            income: {
                ...income,
                otherincome: income.otherincome.filter((item) => !isIncompleteOtherIncome(item)),
            },
            serveArmedForces: serveArmedForces === "yes" ? "yes" : "no",
        })
    }

    const employmentModal = () => (
        <Modal
            className="employment-modal"
            size="lg"
            show={showModal}
            onHide={closeModal}
        >
            <Modal.Header closeButton>
                <Modal.Title>{editIndex >= 0 ? "Edit other income" : "Add other income"}</Modal.Title>
            </Modal.Header>
            <Form noValidate onSubmit={saveDraft}>
                <Modal.Body style={{ padding: "0 40px 14px", maxHeight: "80vh", minHeight: "500px", overflow: "auto" }}>
                    <div style={{ fontSize: "14px", lineHeight: "20px", color: "#6E6E70", margin: "14px 0 0px 0" }}>
                        * Indicates required
                    </div>
                    <Form.Row>
                        <Col xs="12" sm="6" style={{ width: "100%" }} className="mt-4" data-field="amount">
                            <Form.Group controlId="overtime" className="text-left">
                                <Form.Label className="text-left text-nowrap employment-label">*Amount</Form.Label>
                                <DollarInputV2
                                    suffix="Monthly"
                                    onChange={(value) => {
                                        setDraft((prev) => ({ ...prev, amount: value < 0 ? "0" : `${value}` }))
                                        clearModalFieldError("amount")
                                    }}
                                    value={draft.amount}
                                />
                                <FieldError message={modalFieldErrors.amount} />
                            </Form.Group>
                        </Col>
                        <Col xs="12" sm="6" className="mt-4" data-field="incometype">
                            <Form.Group controlId="description" className="text-left">
                                <Form.Label className="text-left text-nowrap employment-label">*What's the type of this income?</Form.Label>
                                <Select
                                    className="w-100"
                                    style={{ height: 44, width: "100%" }}
                                    status={modalFieldErrors.incometype ? "error" : undefined}
                                    value={draft.incometype || undefined}
                                    onChange={(value) => {
                                        setDraft((prev) => ({
                                            ...prev,
                                            incometype: value,
                                            description: incomeDescriptionFromType(value),
                                        }))
                                        clearModalFieldError("incometype")
                                    }}
                                >
                                    {com.othersouresofincome.map((source, index) => (
                                        <Select.Option value={source[0]} key={index}>
                                            {source[1]}
                                        </Select.Option>
                                    ))}
                                </Select>
                                <FieldError message={modalFieldErrors.incometype} />
                            </Form.Group>
                        </Col>
                    </Form.Row>
                </Modal.Body>
                <Modal.Footer>
                    <BootstrapButton type="submit" variant="zeitro-primary" className="income-save-btn" disabled={saving}>
                        Save
                    </BootstrapButton>
                </Modal.Footer>
            </Form>
        </Modal>
    )

    return (
        <>
            <div className="text-left income-application newInterviewPaneProperty application-v2-form-body">
                <div className="page-header page-header-row">
                    <span className="page-header-primary">Income</span>
                    <span className="page-header-separator">/</span>
                    <span className="page-header-secondary">Other income</span>
                </div>

                {employmentModal()}

                <Form>
                    <div className="mt-4 text-left">
                        <Row>
                            <Col className="px-0" md={12} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>
                                        Do you have any other sources of income?{" "}
                                        <Tips
                                            content="You don't need to provide information about alimony or child support unless you want it considered in determining your qualification for the loan."
                                            placement="top"
                                            title=""
                                        />
                                    </Form.Label>
                                    <Row>
                                        <Col className="pl-0" md={6} xs={12}>
                                            <Form.Group className="inputSection">
                                                <CheckGroup
                                                    data={[
                                                        { value: "true", label: "Yes" },
                                                        { value: "false", label: "No" },
                                                    ]}
                                                    onChangeSelect={setHasOtherIncome}
                                                    value={hasOtherIncome ? "true" : "false"}
                                                    type="radio"
                                                    direction="horizontal"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div data-field="otherincome">
                            {income.otherincome.map((item, index) => (
                                <div style={{ paddingRight: "15px" }} className="employment-list mt-3" key={item.key || index}>
                                    <div className="employment-item income-item">
                                        <div className="employment-top">
                                            <div className="employment-top-l">
                                                <div className="employment-icon">
                                                    <img src="/images/employmentIcon.png" alt="employment" />
                                                </div>
                                                <span className="employment-name">Other income #{index + 1}</span>
                                            </div>
                                        </div>
                                        <Row className="employment-form mb-2">
                                            <Col className="employment-form-item mb-2" md={6}>
                                                <div className="employment-form-label">Amount<span className="employment-form-label-colon">:</span></div>
                                                <div className="employment-form-value">{formatAmount(item.amount)} ({item.period || "monthly"})</div>
                                            </Col>
                                            <Col className="employment-form-item mb-2" md={6}>
                                                <div className="employment-form-label">Income type<span className="employment-form-label-colon">:</span></div>
                                                <div className="employment-form-value">{incomeTypeLabel(item)}</div>
                                            </Col>
                                        </Row>
                                        <div className="employment-bottom">
                                            <div className="employment-bottom-left">
                                                <AntButton className="employment-edit-btn" onClick={() => openModal(index)}>Edit</AntButton>
                                                <AntButton className="employment-remove-btn" type="text" onClick={() => removeOtherIncome(index)}>Remove</AntButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <FieldError message={fieldErrors.otherincome} />
                        </div>

                        {income.otherincome.length > 0 && income.otherincome.length < 3 && (
                            <div className="add-job" style={{ marginTop: "12px" }} onClick={() => openModal()}>
                                <span>Add another source of income</span>
                            </div>
                        )}

                        <Row>
                            <Col className="mt-4 px-0" md={12} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>Do you have income from dividends or interest (Form 1099-DIV or Form 1099-INT)?</Form.Label>
                                    <Row>
                                        <Col className="pl-0" md={6} xs={12}>
                                            <Form.Group className="inputSection">
                                                <CheckGroup
                                                    data={[
                                                        { value: "true", label: "Yes" },
                                                        { value: "false", label: "No" },
                                                    ]}
                                                    onChangeSelect={setHasDividends}
                                                    value={income.hasdividends ? "true" : "false"}
                                                    type="radio"
                                                    direction="horizontal"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col className="mt-4 px-0" md={12} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>
                                        Have you or your deceased spouse ever served, or are you currently serving, in the United States Armed Forces?
                                    </Form.Label>
                                    <Row>
                                        <Col className="pl-0" md={6} xs={12}>
                                            <Form.Group className="inputSection">
                                                <CheckGroup
                                                    data={[
                                                        { value: "yes", label: "Yes" },
                                                        { value: "no", label: "No" },
                                                    ]}
                                                    onChangeSelect={setServeArmedForces}
                                                    value={serveArmedForces === "yes" ? "yes" : "no"}
                                                    type="radio"
                                                    direction="horizontal"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>
                </Form>
            </div>

            <Row className="text-left application-bottom-bar">
                <div className="d-flex align-items-center">
                    <BootstrapButton
                        id="previous"
                        type="button"
                        variant="zeitro-primary"
                        disabled={saving}
                        className="operate-btn back mr-3"
                        onClick={onBack}
                    >
                        Back
                    </BootstrapButton>
                    <BootstrapButton
                        id="next"
                        type="button"
                        variant="zeitro-primary"
                        disabled={saving}
                        className="text-center operate-btn btn"
                        onClick={handleNext}
                    >
                        Next
                    </BootstrapButton>
                </div>
            </Row>
        </>
    )
}

export default OtherIncomeV2
